import { inject, Injectable } from '@angular/core';
import { HttpGatewayService } from './http-gateway.service';
import { Observable, Observer, forkJoin, map } from 'rxjs';
import { ApplicationStore, ApplicationStoreType } from '../store/application.store';
import {
    HotelAccess,
    MfaType,
    SignedInUser,
    SignedInUserContract,
    User,
    UserAccessUpdateRequest,
    UserOrganizationAccess,
    UserProfileUpdateRequest,
} from '../../shared/types/user';
import { Department } from '../../shared/types/department';
import { Role } from '../../shared/types/role';
import { PropertyPrivilege } from '../../shared/types/property-privilege';
import { UrlBuilder } from '../../shared/utils/url-utils';
import { PropertyForInvitesResponseContract } from '../../settings/properties/types/property';
import { UserUpdateResponseContract } from '../../reset-password/services/password-reset.service';

export interface UpdateUserData {
    firstName: string;
    lastName: string;
    department: Department | null;
    role: Role;
    propertyPrivileges: PropertyPrivilege[];
    allHotels: HotelAccess[];
}

export const USER_URLS = {
    GET_USER: 'api/user',
    UPDATE_USER: 'api/user/update-user/{userId}',
    GET_USERS: `api/v5/organization/{organizationId}/manage-users/get-org-users`,
    GET_USERS_ACCESS: 'api/v5/organization/{organizationId}/manage-users/get-available-properties/{userId}',
    GET_USER_MFA_STATUS: 'api/v5/organization/{organizationId}/manage-users/get-mfa-status',
    GET_USER_MFA_TYPES: 'api/v5/organization/{organizationId}/manage-users/get-mfa-types/{userId}',
    SAVE_ACCESS_CHANGES: 'api/v5/organization/{organizationId}/manage-users/save-access-changes/{userId}',
    SAVE_PROFILE_CHANGES: 'api/v5/organization/{organizationId}/manage-users/save-profile-changes/{userId}',
    SAVE_MFA_CHANGES: 'api/v5/organization/{organizationId}/manage-users/enable-email-mfa/{userId}',
    GLOBAL_SIGN_OUT: 'api/v5/organization/{organizationId}/manage-users/global-sign-out/{userId}',
    REMOVE_USER: 'api/v5/organization/{organizationId}/manage-users/remove-user/{userId}',
    GET_PROPERTIES_FOR_INVITE: 'api/v5/organization/{organizationId}/manage-users/get-available-properties-invite',
};

@Injectable({ providedIn: 'root' })
export class UserService {
    private http = inject(HttpGatewayService);
    private applicationStore: ApplicationStoreType = inject(ApplicationStore);

    public loadUser(forceRefresh: boolean = false): Observable<SignedInUser | null> {
        return new Observable((observer: Observer<SignedInUser | null>) => {
            const currentUser = this.applicationStore.signedInUser();
            if (forceRefresh || !currentUser) {
                this.getSignedInUser().subscribe({
                    next: (user) => {
                        this.applicationStore.setSignedInUser(user);
                        observer.next(user);
                        observer.complete();
                    },
                    error: () => {
                        observer.next(null);
                        observer.complete();
                    },
                });
            } else {
                observer.next(currentUser);
                observer.complete();
            }
        });
    }

    public getUsers(organizationId: string): Observable<User[]> {
        const url = new UrlBuilder(USER_URLS.GET_USERS).addOrganizationId(organizationId).toUrl();

        return this.http.get(url);
    }

    public getUserAccess(organizationId: string, userId: number): Observable<UserOrganizationAccess> {
        const url = new UrlBuilder(USER_URLS.GET_USERS_ACCESS)
            .addOrganizationId(organizationId.toString())
            .addUserId(userId.toString())
            .toUrl();

        return this.http.get(url);
    }

    public getUserMfaRequired(organizationId: string): Observable<{ mfaRequired: boolean }> {
        const url = new UrlBuilder(USER_URLS.GET_USER_MFA_STATUS)
            .addOrganizationId(organizationId.toString())
            .toUrl();

        // misleading response, mfaEnabled actually means that mfa is required for the user by 1 or more organizations
        return this.http
            .get<{ mfaEnabled: boolean }>(url)
            .pipe(map((response) => ({ mfaRequired: response.mfaEnabled })));
    }

    public getUserMfaSettings(organizationId: string, userId: number): Observable<MfaType[]> {
        const url = new UrlBuilder(USER_URLS.GET_USER_MFA_TYPES)
            .addOrganizationId(organizationId.toString())
            .addUserId(userId.toString())
            .toUrl();

        return this.http.get(url);
    }

    public enableEmailMfa(organizationId: string, userId: number): Observable<void> {
        const url = new UrlBuilder(USER_URLS.SAVE_MFA_CHANGES)
            .addOrganizationId(organizationId.toString())
            .addUserId(userId.toString())
            .toUrl();

        return this.http.post(url, {});
    }

    public updateUser(organizationId: string, userId: number, data: UpdateUserData): Observable<void> {
        const profileRequest: UserProfileUpdateRequest = {
            userId,
            firstName: data.firstName,
            lastName: data.lastName,
            department: data.department as Department,
        };

        const privilegeMap = new Map(data.propertyPrivileges.map((p) => [p.propertyId, p]));
        const accessRequest: UserAccessUpdateRequest = {
            userId,
            orgId: organizationId,
            role: data.role,
            properties: {
                hotels: data.allHotels.map((h) => {
                    const priv = privilegeMap.get(h.id);
                    return {
                        id: h.id,
                        owned: priv?.isAdmin ?? false,
                        enabled: !!priv,
                        dataSources: h.dataSources.map((ds) => ({
                            code: ds.code,
                            enabled: priv?.dataAccess.includes(ds.code) ?? false,
                        })),
                    };
                }),
            },
        };

        const profileUrl = new UrlBuilder(USER_URLS.SAVE_PROFILE_CHANGES)
            .addOrganizationId(organizationId)
            .addUserId(userId.toString())
            .toUrl();
        const accessUrl = new UrlBuilder(USER_URLS.SAVE_ACCESS_CHANGES)
            .addOrganizationId(organizationId)
            .addUserId(userId.toString())
            .toUrl();

        return forkJoin([
            this.http.post<void>(profileUrl, profileRequest),
            this.http.post<void>(accessUrl, accessRequest),
        ]).pipe(map(() => undefined));
    }

    public removeUserFromOrganization(organizationId: string, userId: string): Observable<void> {
        const url = new UrlBuilder(USER_URLS.REMOVE_USER)
            .addOrganizationId(organizationId)
            .addUserId(userId.toString())
            .toUrl();

        return this.http.delete(url);
    }

    public getPropertiesForInvites(organizationId: string): Observable<PropertyForInvitesResponseContract> {
        const url = new UrlBuilder(USER_URLS.GET_PROPERTIES_FOR_INVITE).addOrganizationId(organizationId).toUrl();

        return this.http.get<PropertyForInvitesResponseContract>(url, true);
    }

    public updateUserInfo(
        id: number,
        firstName: string,
        lastName: string,
        department?: Department,
    ): Observable<UserUpdateResponseContract> {
        const url = new UrlBuilder(USER_URLS.UPDATE_USER).addUserId(id.toString()).toUrl();
        const request = {
            firstName,
            lastName,
            department,
        };
        return this.http.post(url, request);
    }

    public userGlobalSignOut(organizationId: string, userId: number): Observable<void> {
        const url = new UrlBuilder(USER_URLS.GLOBAL_SIGN_OUT)
            .addOrganizationId(organizationId)
            .addUserId(userId.toString())
            .toUrl();
        return this.http.post(url, {});
    }

    private getSignedInUser(): Observable<SignedInUser> {
        return this.http.get<SignedInUserContract>(USER_URLS.GET_USER).pipe(map((user) => this.toSignedInUser(user)));
    }

    private toSignedInUser(user: SignedInUserContract): SignedInUser {
        return new SignedInUser(
            user.id,
            user.username,
            user.firstName,
            user.lastName,
            user.email,
            user.fullName,
            user.active,
            user.department,
        );
    }
}
